import React, { useEffect, useState, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { RadioGroup, FormControlLabel, Radio, Typography, Switch, Stack, Paper, Checkbox, Box, Autocomplete, TextField, InputAdornment, IconButton, Collapse, Link as MuiLink } from '@mui/material';

import { ProfileFormData, StepProps, useStep } from '../wizard/QuestionsForm'
import { GetTitleFromTitleSubtitle, GetSubTitleFromTitleSubtitle, yesNoErrorMessage } from '../wizard/QuestionsForm'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { SmsPhoneValidation, PhoneVerificationData } from './SmsPhoneValidation';

import { useAuthProfile } from '../../app/hooks';
import { CUSTOM_METHOD } from '../../app/auth/utils';

const top100Films = [
  { label: '0-10', from: 0, to: 10 },
  { label: '11-20', from: 11, to: 20 },
  { label: '21-30', from: 21, to: 30 },
  { label: '31-40', from: 31, to: 40 },
  { label: '41-50', from: 41, to: 50 },
  { label: '51-60', from: 51, to: 60 },
  { label: '61-70', from: 61, to: 70 },
  { label: '71-80', from: 71, to: 80 },
  { label: '81-90', from: 81, to: 90 },
  { label: '91+', from: 90, },
]

export const Communicate: React.FC = () => {
  const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile, /*userAttributesStateStatus, */  authMethod, customAuth, sub } = useAuthProfile()
  const { step, data, updatedData, resetNextPressed, nextPressed, onSubmitProfile, setStepBusy, setStepBackHandler } = useStep()
  const { trigger, control, formState: { errors }, setValue, getValues, clearErrors, watch, handleSubmit } = useFormContext<ProfileFormData>();
  const formData = getValues();
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const [showCommMethods, setShowCommMethods] = useState(false)
  //const [triggerVerification, setTriggerVerification] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const hasTriggeredSubmissionRef = useRef(false);

  useEffect(() => {
    setShowCommMethods(data.communicate_with_others === 'Y')
  }, [data.receive_emails])

  const communicateByMessenger = watch("communicate_by_messenger");
  const phoneValue = watch("phone") as PhoneVerificationData | undefined;

  // Canonical 10-digit form: strip everything non-numeric and drop a leading
  // country `1`. Used to compare an edit against the originally saved phone.
  const canonicalDigits = (s: string | undefined | null): string => {
    const d = (s ?? '').replace(/\D/g, '');
    return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  };
  const originalDigits = canonicalDigits((data as any)?.phone);

  const handlePhoneChange = (newPhoneData: PhoneVerificationData) => {
    // If the edited phone canonicalizes to the original saved phone, treat it
    // as still verified — user reverted to a previously verified number, no
    // need to re-send SMS. Both sides must be complete 10-digit numbers to
    // count as a match (so empty/partial edits never sneak through).
    const editedDigits = canonicalDigits(newPhoneData.phone);
    const matchesOriginal =
      editedDigits.length === 10 &&
      originalDigits.length === 10 &&
      editedDigits === originalDigits;
    const corrected: PhoneVerificationData = matchesOriginal
      ? { ...newPhoneData, verificationComplete: true }
      : newPhoneData;

    setValue("phone", corrected, { shouldDirty: true });
    setValue("communicate_by_messenger", corrected.consentChecked, { shouldDirty: true });

    // Only auto-trigger validation (which fires the auto-submit in the validate
    // rule) on a fresh SMS verification — not on our match-original override.
    // Otherwise typing back to the saved phone would auto-advance the wizard
    // without the user pressing Next.
    if (newPhoneData.verificationComplete) {
      trigger("phone");
    }
  };

  const externalValidatePhone = (data: PhoneVerificationData) => {
    // Accept any format the value might arrive in: `+18005551234` from the DB,
    // `(800) 555-1234` from imask, `8005551234` raw. Reduce to canonical 10 digits.
    let phoneDigits = (data?.phone ?? '').replace(/\D/g, '');
    if (phoneDigits.length === 11 && phoneDigits.startsWith('1')) {
      phoneDigits = phoneDigits.slice(1);
    }

    // Opted in: phone is required and must be a complete 10 digits.
    if (data?.consentChecked === true) {
      if (phoneDigits.length !== 10) {
        return { isValid: false, errorMessage: "Valid 10-digit phone number required" };
      }
    } else {
      // Not opted in (unchecked): phone is optional. Empty is fine; partial entry
      // is the only thing we flag.
      if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
        return { isValid: false, errorMessage: "If you provide a phone number, it must be a complete 10-digit number" };
      }
    }

    return { isValid: true, errorMessage: "" };
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: "space-between",
      height: '100%',
    }}>
      <div>
        <Box sx={{
          mb: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          backgroundColor: 'rgba(101, 110, 255, 0.06)',
          borderLeft: '3px solid #656EFF',
        }}>
          <Typography variant="body2" sx={{ color: '#444', fontWeight: 500 }}>
            Add your phone number to interact with Patient Connect just by texting
          </Typography>
        </Box>
        <Box sx={{
          borderRadius: 2,
          backgroundColor: 'rgba(101, 110, 255, 0.04)',
          border: '1px solid rgba(164, 142, 233, 0.12)',
          p: 1.5,
          mb: 1.5,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>
            (Optional) Connect via text to:
          </Typography>
          <Stack spacing={0.25}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CheckCircleIcon sx={{ color: '#656EFF', fontSize: 16 }} />
              <Typography variant="caption">Chat with other members securely</Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CheckCircleIcon sx={{ color: '#656EFF', fontSize: 16 }} />
              <Typography variant="caption">Ask questions with Knowledge Connect</Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CheckCircleIcon sx={{ color: '#656EFF', fontSize: 16 }} />
              <Typography variant="caption">Add to your journal</Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" sx={{ mt: 0.75, display: 'block', fontWeight: 600, color: '#656EFF' }}>
            Your phone number will never be shared.
          </Typography>
        </Box>
        <Paper elevation={0} sx={{ width: '100%', backgroundColor: 'transparent' }}>
          <Controller
            name="phone"
            control={control}
            defaultValue={{ phone: "", verificationComplete: false, consentChecked: undefined }}
            rules={{
              validate: (value) => {
                if (!value) {
                  return "Phone verification data is required";
                }
                const result = externalValidatePhone(value);
                if (!result.isValid) {
                  return result.errorMessage;
                }
                
                // VERIFICATION COMPLETE - TRIGGER SUBMISSION
                if (value?.consentChecked && value?.verificationComplete  && onSubmitProfile && !hasTriggeredSubmissionRef.current) {
                  hasTriggeredSubmissionRef.current = true;
                  handleSubmit(onSubmitProfile)();
                }
                return true;
              }
            }}
            render={({ field }) => (
              <SmsPhoneValidation
                value={{
                  phone: field.value?.phone ?? '',
                  verificationComplete: field.value?.verificationComplete ?? false,
                  consentChecked: field.value?.consentChecked ?? undefined
                }}
                onChange={handlePhoneChange}
                onBlur={field.onBlur}
                error={!!errors.phone}
                helperText={errors.phone?.message as string}
                disabled={authMethod === CUSTOM_METHOD}
                required={phoneValue?.consentChecked}
                label={`${authMethod === CUSTOM_METHOD ? "This" : "(Optional)"} Phone Number${authMethod === CUSTOM_METHOD ? " was used to log in and can't be changed" : ""}`}
                showVerificationButtons={false}
                validatePhone={externalValidatePhone}
                nextPressed={updatedData?.nextPressed}
                onBusyChange={setStepBusy}
                setStepBackHandler={setStepBackHandler}
              />
            )}
          />
        </Paper>
      </div>
    </div>
  )
}